import {alert_atom} from 'atoms';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useRecoilState} from 'recoil';
import {AlertSeverity, IAlertState} from 'types';

export function useAlert() {
	const [alert, setAlert] = useRecoilState(alert_atom);

	const showAlert = (message: string, severity: AlertSeverity = 'info') => {
		setAlert((prevAlert: IAlertState) => {
			if (prevAlert.open) return {...prevAlert, message};
			else return {open: true, message, severity};
		});
	};

	const hideAlert = () => setAlert((prevAlert: IAlertState) => ({...prevAlert, open: false}));

	return {alert, showAlert, hideAlert};
}

export default function useSound(soundUrl: string, options: {volume?: number; interrupt?: boolean} = {}) {
	const {volume = 1, interrupt = true} = options;
	const audioRef = useRef<HTMLAudioElement | null>(null);

	useEffect(() => {
		audioRef.current = new Audio(soundUrl);
		audioRef.current.volume = volume;

		return () => {
			if (audioRef.current) {
				audioRef.current.pause();
				audioRef.current = null;
			}
		};
	}, [soundUrl, volume]);

	const play = useCallback(async () => {
		if (!audioRef.current) return;

		try {
			if (interrupt || audioRef.current.ended) audioRef.current.currentTime = 0;
			await audioRef.current.play();
		} catch (error) {
			console.error('Failed to play sound:', error);
		}
	}, [interrupt]);

	const stop = useCallback(() => {
		if (audioRef.current) {
			audioRef.current.pause();
			audioRef.current.currentTime = 0;
		}
	}, []);

	return {play, stop};
}

export function usePullToRefresh(
	onRefresh: () => Promise<void>,
	options: {
		threshold?: number;
		maxPull?: number;
		baseResistance?: number;
	} = {},
): {
	containerRef: React.RefObject<HTMLDivElement>;
	isPulling: boolean;
	pullDistance: number;
} {
	const {threshold = 20, maxPull = 150, baseResistance = 0.4} = options;
	const [isPulling, setIsPulling] = useState(false);
	const [pullDistance, setPullDistance] = useState(0);
	const touchStartY = useRef(0);
	const containerRef = useRef<HTMLDivElement>(null);

	const handleTouchStart = useCallback((e: Event) => {
		const touchEvent = e as TouchEvent;
		touchStartY.current = touchEvent.touches[0].clientY;
	}, []);

	const handleTouchMove = useCallback(
		(e: Event) => {
			const touchEvent = e as TouchEvent;
			const container = containerRef.current;
			if (!container && window.scrollY > 2) return;
			const isAtTop = container ? container.scrollTop <= 2 : window.scrollY <= 2;

			if (isAtTop) {
				const currentY = touchEvent.touches[0].clientY;
				const diff = currentY - touchStartY.current;

				if (diff > 0) {
					const resistance = baseResistance - Math.min(diff, maxPull) / 500;
					const distance = diff * resistance;
					setPullDistance(distance);

					if (touchEvent.cancelable) {
						touchEvent.preventDefault();
					}
				}
			}
		},
		[baseResistance, maxPull],
	);

	const handleTouchEnd = useCallback(
		async (e: Event) => {
			if (pullDistance > threshold) {
				setIsPulling(true);
				try {
					await onRefresh();
				} catch (error) {
					console.error('Refresh failed:', error);
				} finally {
					setIsPulling(false);
					setPullDistance(0);
				}
			} else {
				setPullDistance(0);
			}
		},
		[pullDistance, threshold, onRefresh],
	);

	useEffect(() => {
		const container = containerRef.current || document;

		container.addEventListener('touchstart', handleTouchStart, {passive: false});
		container.addEventListener('touchmove', handleTouchMove, {passive: false});
		container.addEventListener('touchend', handleTouchEnd);

		return () => {
			container.removeEventListener('touchstart', handleTouchStart);
			container.removeEventListener('touchmove', handleTouchMove);
			container.removeEventListener('touchend', handleTouchEnd);
		};
	}, [handleTouchStart, handleTouchMove, handleTouchEnd]);

	return {containerRef, isPulling, pullDistance};
}
